/**
 * Срок задачи в том виде, в каком он стоит на карточке: «12 авг».
 *
 * Через `Intl`, а не через dayjs с локалями: формат в макете — день и короткий
 * месяц, ровно то, что платформа умеет сама и на трёх языках сразу.
 */

/** `vn` — код страны в наших локалях; у языка он `vi`, и Intl знает только его */
const INTL_LOCALES: Record<string, string> = { vn: 'vi' }

/**
 * Год добавляем, только когда он не текущий: «12 авг» у задачи из следующего
 * декабря читалось бы как ближайший август, а год у каждого срока — это лишняя
 * колонка в футере, которого и так впритык.
 */
/**
 * Русский отдаёт «12 авг. 2027 г.» — в макете ни сокращения года, ни точки
 * после месяца. Пробел перед «г.» обязателен: без него шаблон съедает хвост
 * самого «авг.».
 */
const clean = (text: string): string => text.replace(/\s+г\.$/, '').replace(/\./g, '')

export const formatDueDate = (iso: string, locale: string, now = new Date()): string => {
  if (!iso) return ''

  const date = new Date(iso)

  if (Number.isNaN(date.getTime())) return ''

  const sameYear = date.getFullYear() === now.getFullYear()

  return clean(
    new Intl.DateTimeFormat(INTL_LOCALES[locale] ?? locale, {
      day: 'numeric',
      month: 'short',
      ...(sameYear ? {} : { year: 'numeric' }),
    }).format(date),
  )
}

/**
 * Момент времени в день для календаря — `YYYY-MM-DD` в поясе человека.
 *
 * Срок «20 августа» лежит на сервере началом дня в Ханое, то есть 17:00
 * девятнадцатого по UTC, и `toISOString` вернул бы из него 19-е: календарь
 * подсвечивал бы вчерашнюю дату, а сохранение сдвигало бы срок на день назад.
 */
export const toDateInput = (iso: string): string => {
  if (!iso) return ''

  const date = new Date(iso)

  if (Number.isNaN(date.getTime())) return ''

  return new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 10)
}

const startOfDay = (date: Date): number =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()

/**
 * Момент события — «сегодня, 09:15», «вчера, 18:42», «4 авг 2026».
 *
 * Свежее время читают по часам, старое — по дате: «11 авг, 09:15» у вчерашней
 * правки заставляет вспоминать, какое сегодня число. Границу держим на сутках,
 * дальше час уже ничего не решает.
 *
 * Слова «сегодня» и «вчера» приходят снаружи: формат живёт вне компонента, а
 * переводы — в словаре.
 */
export const formatStamp = (
  iso: string,
  locale: string,
  labels: { today: string; yesterday: string },
  now = new Date(),
): string => {
  if (!iso) return ''

  const date = new Date(iso)

  if (Number.isNaN(date.getTime())) return ''

  const days = Math.round((startOfDay(now) - startOfDay(date)) / 86_400_000)

  if (days === 0 || days === 1) {
    const time = new Intl.DateTimeFormat(INTL_LOCALES[locale] ?? locale, {
      hour: '2-digit',
      minute: '2-digit',
    }).format(date)

    return `${days === 0 ? labels.today : labels.yesterday}, ${time}`
  }

  // Год у давнего события ставим всегда: «4 авг» без года у задачи, заведённой
  // в позапрошлом августе, читается как этот.
  return clean(
    new Intl.DateTimeFormat(INTL_LOCALES[locale] ?? locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(date),
  )
}
